import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import HeroSection from "@/components/feature/HeroSection";
import TrustSection from "@/components/feature/TrustSection";
import FeatureGrid from "@/components/feature/FeatureGrid";
import DarkCategorySection from "@/components/feature/DarkCategorySection";
import MoreCategories from "@/components/feature/MoreCategories";
import FeaturesSection from "@/components/feature/FeaturesSection";
import ComparisonSection from "@/components/feature/ComparisonSection";
import ArticlePreview from "@/components/feature/ArticlePreview";
import SafetySection from "@/components/feature/SafetySection";
import FAQSection from "@/components/feature/FAQSection";
import UpdatesSection from "@/components/feature/UpdatesSection";
import CTASection from "@/components/feature/CTASection";
import Footer from "@/components/feature/Footer";
import { faqItems } from "@/mocks/seo-data";
import { Link } from "react-router-dom";

const SITE_URL = import.meta.env.VITE_SITE_URL || "https://usfansspreadsheet.blog";

export default function Home() {
  const { t } = useTranslation("common");

  useEffect(() => {
    document.title = "USFansSpreadsheet – Poradnik, Opinie i Aktualizacje 2026";
  }, []);

  return (
    <>
      <Navbar />
      <main>
        {/* Hero */}
        <HeroSection />

        {/* Trust + What is */}
        <TrustSection />

        {/* Feature Grid - Categories */}
        <FeatureGrid id="kategorie" />

        {/* Features list */}
        <FeaturesSection />

        {/* Dark Category Directory */}
        <DarkCategorySection />

        {/* More Categories */}
        <MoreCategories />

        {/* Comparison Table */}
        <ComparisonSection />

        {/* Article Preview with carousel */}
        <ArticlePreview />

        {/* Safety */}
        <SafetySection />

        {/* FAQ */}
        <FAQSection />

        {/* Updates Timeline */}
        <UpdatesSection />

        {/* CTA Split */}
        <CTASection />

        {/* EEAT / Author */}
        <section className="bg-background-50 py-12 px-4 md:px-6 border-t border-background-200">
          <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-accent-100 flex items-center justify-center">
                <i className="ri-user-line text-accent-600 text-xl"></i>
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground-900">
                  Ekspert ds. narzędzi online
                </p>
                <p className="text-xs text-foreground-500">
                  Ostatnia aktualizacja: Czerwiec 2026
                </p>
              </div>
            </div>
            <Link
              to="/artykul/usfansspreadsheet-poradnik-2026"
              className="text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              Zobacz poradnik
              <i className="ri-arrow-right-line ml-1"></i>
            </Link>
          </div>
        </section>
      </main>

      {/* Footer */}
      <Footer />

      {/* Organization Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "USFansSpreadsheet",
            url: SITE_URL,
            description: "Kompletny przewodnik, porównania i aktualizacje USFansSpreadsheet 2026",
            sameAs: [
              "https://kaspreadsheet.com",
            ],
          }),
        }}
      />

      {/* WebSite Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            name: "USFansSpreadsheet",
            url: SITE_URL,
            description: "Kompletny przewodnik, porównania i aktualizacje USFansSpreadsheet 2026",
            inLanguage: "pl-PL",
          }),
        }}
      />

      {/* BreadcrumbList Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "USFansSpreadsheet",
                item: SITE_URL,
              },
            ],
          }),
        }}
      />

      {/* FAQPage Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqItems.map((item) => ({
              "@type": "Question",
              name: item.question,
              acceptedAnswer: {
                "@type": "Answer",
                text: item.answer,
              },
            })),
          }),
        }}
      />
    </>
  );
}